import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import Button from 'common/components/button/Button';
import Modal, { ModalHeader, ModalBody, ModalFooter } from 'common/components/modal/Modal';
import Toast from 'common/services/Toast';
import { UnitDto } from 'api/units/models/UnitDto';
import { BudgetLineDto } from 'api/budgets/models/BudgetDto';
import BudgetsService from 'api/budgets/BudgetsService';
import { setLines, calculateAllLinesTotals } from 'store/budget/action';
import { Reducers } from 'store/types';
import StepUpload from './steps/StepUpload';
import StepMapping from './steps/StepMapping';
import { buildBudgetLines } from './utils/lineBuilder';
import { ExcelSheetData, ImportRow, ColumnMappingEntry, BudgetImportField, BudgetColumnMapping, BUDGET_IMPORT_FIELDS } from './utils/types';
import styles from './ImportStructureModal.module.scss';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    units: UnitDto[];
    externalPartId?: string | null;
}

function trimTrailingEmptyRows (rows: ImportRow[]): ImportRow[] {
    let lastNonEmpty = rows.length - 1;
    while (lastNonEmpty >= 0) {
        const row = rows[lastNonEmpty];
        const hasData = row.rawCells.some(cell => cell?.trim());
        if (hasData || row.type) break;
        lastNonEmpty--;
    }
    return rows.slice(0, lastNonEmpty + 1);
}

function applyPreviousMapping (mappings: ColumnMappingEntry[], prev: BudgetColumnMapping | null): ColumnMappingEntry[] {
    if (!prev?.mappings?.length) return mappings;

    const usedFields = new Set<BudgetImportField>();
    const result = mappings.map(m => {
        // Match only by column name
        const match = prev.mappings.find(
            pm => pm.budgetField
                && !usedFields.has(pm.budgetField)
                && pm.excelColumnName && m.excelColumnName
                && pm.excelColumnName.toLowerCase() === m.excelColumnName.toLowerCase()
        );
        if (match?.budgetField) {
            usedFields.add(match.budgetField);
            return { ...m, budgetField: match.budgetField };
        }
        return m;
    });
    return result;
}

const ImportStructureModal: React.FC<Props> = ({ isOpen, onClose, units, externalPartId }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const form = useFormContext();
    const existingLines = useSelector((state: Reducers) => state.budget.lines);

    const [step, setStep] = useState(0);
    const [excelData, setExcelData] = useState<ExcelSheetData | null>(null);
    const [headerRowNumber, setHeaderRowNumber] = useState(1);
    const [columnMappings, setColumnMappings] = useState<ColumnMappingEntry[]>([]);
    const [importRows, setImportRows] = useState<ImportRow[]>([]);
    const [fileName, setFileName] = useState('');
    const [previousMapping, setPreviousMapping] = useState<BudgetColumnMapping | null>(null);

    useEffect(() => {
        if (!isOpen) return;

        // First try current budget's mapping from the form
        const currentJson = form.getValues('columnMappingJson');
        if (currentJson) {
            try {
                const mapping = typeof currentJson === 'string' ? JSON.parse(currentJson) : currentJson;
                setPreviousMapping(mapping as BudgetColumnMapping);
                return;
            } catch {
                // ignore
            }
        }

        // Then try to get from another budget of the same client
        if (externalPartId) {
            BudgetsService.getLastColumnMapping(externalPartId).then(result => {
                if (result) {
                    try {
                        const mapping = typeof result === 'string' ? JSON.parse(result) : result;
                        setPreviousMapping(mapping as BudgetColumnMapping);
                    } catch {
                        // ignore parse errors
                    }
                }
            }).catch(() => {
                // ignore API errors
            });
        }
    }, [isOpen]);

    // When previousMapping arrives and we already have excel data, re-apply
    useEffect(() => {
        if (previousMapping && excelData) {
            const headerRow = previousMapping.headerRowNumber && previousMapping.headerRowNumber <= excelData.rows.length
                ? previousMapping.headerRowNumber
                : headerRowNumber;

            if (headerRow !== headerRowNumber) {
                setHeaderRowNumber(headerRow);
            }

            const headers = excelData.rows[headerRow - 1] ?? [];
            let mappings: ColumnMappingEntry[] = headers.map((cell, idx) => ({
                excelColumnIndex: idx,
                excelColumnName: cell,
                budgetField: null as BudgetImportField | null,
            }));
            mappings = applyPreviousMapping(mappings, previousMapping);
            setColumnMappings(mappings);

            const dataRows = excelData.rows.slice(headerRow);
            const rows: ImportRow[] = dataRows.map((cells, idx) => ({
                rowIndex: idx,
                type: cells.some(c => c?.trim()) ? 'S' : null,
                rawCells: cells,
            }));
            setImportRows(trimTrailingEmptyRows(rows));
        }
    }, [previousMapping]);

    const initMappingData = useCallback((data: ExcelSheetData, headerRow: number, prevMapping: BudgetColumnMapping | null) => {
        const headerRowIdx = headerRow - 1;
        const headers = data.rows[headerRowIdx] ?? [];

        let mappings: ColumnMappingEntry[] = headers.map((cell, idx) => ({
            excelColumnIndex: idx,
            excelColumnName: cell,
            budgetField: null as BudgetImportField | null,
        }));

        mappings = applyPreviousMapping(mappings, prevMapping);

        const dataRows = data.rows.slice(headerRow);
        const rows: ImportRow[] = dataRows.map((cells, idx) => ({
            rowIndex: idx,
            type: cells.some(c => c?.trim()) ? 'S' : null,
            rawCells: cells,
        }));

        setColumnMappings(mappings);
        setImportRows(trimTrailingEmptyRows(rows));
    }, []);

    const onFileLoaded = useCallback((data: ExcelSheetData, name: string) => {
        setExcelData(data);
        setFileName(name);
        const row = previousMapping?.headerRowNumber && previousMapping.headerRowNumber <= data.rows.length
            ? previousMapping.headerRowNumber
            : headerRowNumber;
        if (row !== headerRowNumber) {
            setHeaderRowNumber(row);
        }
        initMappingData(data, row, previousMapping);
    }, [headerRowNumber, previousMapping, initMappingData]);

    const onHeaderRowChange = useCallback((row: number) => {
        setHeaderRowNumber(row);
        if (excelData) {
            initMappingData(excelData, row, previousMapping);
        }
    }, [excelData, previousMapping, initMappingData]);

    const onError = useCallback((message: string) => {
        Toast.error(message);
    }, []);

    const onColumnMappingChange = useCallback((colIndex: number, field: BudgetImportField | null) => {
        setColumnMappings(prev => prev.map(m =>
            m.excelColumnIndex === colIndex
                ? { ...m, budgetField: field }
                : m
        ));
    }, []);

    const onRowTypeChange = useCallback((rowIndex: number, type: 'S' | 'O' | null) => {
        setImportRows(prev => prev.map(r =>
            r.rowIndex === rowIndex ? { ...r, type } : r
        ));
    }, []);

    const onCellValueChange = useCallback((rowIndex: number, colIndex: number, value: string) => {
        setImportRows(prev => prev.map(r => {
            if (r.rowIndex !== rowIndex) return r;
            const rawCells = [...r.rawCells];
            rawCells[colIndex] = value;
            return { ...r, rawCells };
        }));
    }, []);

    const canGoNext = excelData != null && excelData.rows.length > 0;
    const mappedFields = new Set(columnMappings.filter(m => m.budgetField).map(m => m.budgetField));
    const allFieldsMapped = BUDGET_IMPORT_FIELDS.every(f => mappedFields.has(f.value));

    const onImport = () => {
        const allLines = buildBudgetLines(importRows, existingLines, units, columnMappings);
        dispatch(setLines(allLines));
        dispatch(calculateAllLinesTotals());

        const mapping: BudgetColumnMapping = {
            mappings: columnMappings.filter(m => m.budgetField),
            headerRowNumber,
            fileName,
            importedAt: new Date().toISOString(),
        };
        form.setValue('columnMappingJson', JSON.stringify(mapping));

        Toast.success(t('budgets.budget.import_structure.import_success'));
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            className={styles.modal}
            style={{ content: { maxWidth: '90vw', width: step === 0 ? '600px' : '1200px' } }}
        >
            <ModalHeader className={styles.header}>
                <h5 className={styles.title}>{t('budgets.budget.import_structure.modal_title')}</h5>
                <button type="button" className="btn-close" onClick={onClose} />
            </ModalHeader>

            <ModalBody className={styles.body}>
                {step === 0 && (
                    <StepUpload
                        excelData={excelData}
                        fileName={fileName}
                        onFileLoaded={onFileLoaded}
                        onError={onError}
                    />
                )}
                {step === 1 && excelData && (
                    <StepMapping
                        excelData={excelData}
                        headerRowNumber={headerRowNumber}
                        columnMappings={columnMappings}
                        importRows={importRows}
                        onColumnMappingChange={onColumnMappingChange}
                        onRowTypeChange={onRowTypeChange}
                        onHeaderRowChange={onHeaderRowChange}
                        onCellValueChange={onCellValueChange}
                    />
                )}
            </ModalBody>

            <ModalFooter className={styles.footer}>
                {step === 1 && (
                    <Button variant="secondary" onClick={() => setStep(0)}>
                        {t('budgets.budget.import_structure.previous')}
                    </Button>
                )}
                <div className={styles.footerSpacer} />
                {step === 0 && (
                    <Button onClick={() => setStep(1)} disabled={!canGoNext}>
                        {t('budgets.budget.import_structure.next')}
                    </Button>
                )}
                {step === 1 && (
                    <Button onClick={onImport} disabled={!allFieldsMapped}>
                        {t('budgets.budget.import_structure.confirm_import')}
                    </Button>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default ImportStructureModal;
